import { Box, Dialog, DialogContent, DialogTitle, Divider, FormControlLabel, Switch, TextField, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useEffect, useState } from "react";
import { useAuth } from "../../../contexts/AuthProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { INK, NEUTRAL } from "../../../theme/tokens";
import axiosInstance from "../../../utils/networkWrapper";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";

type Props = {
	open: boolean;
	onClose: () => void;
	departmentId: string;
	currentDate: DateTime | null;
	scheduleCycleWeeks?: number;
	schedulePublishLeadWeeks?: number;
};

export const StaffScheduleVisibilityPopup: FC<Props> = ({ open, onClose, departmentId, currentDate, scheduleCycleWeeks, schedulePublishLeadWeeks }) => {
	const { showErrorSnackbar, afterScheduleWrite } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { updateUserInfo } = useAuth();
	const [weeks, setWeeks] = useState<number>(0);
	const [isLoading, setIsLoading] = useState(false);
	const [notificationMessage, setNotificationMessage] = useState<string>("");

	const [autoPublishEnabled, setAutoPublishEnabled] = useState(false);
	const [cycleWeeks, setCycleWeeks] = useState<number>(4);
	const [leadWeeks, setLeadWeeks] = useState<number>(2);
	const [isSavingAutoPost, setIsSavingAutoPost] = useState(false);

	useEffect(() => {
		if (open) {
			setNotificationMessage("");
			if (currentDate) {
				const currentWeekSunday = DateTime.now().startOf("week");
				const targetDate = currentDate?.startOf("day");
				if (targetDate.isValid) {
					const targetWeekSunday = targetDate.startOf("week");
					const diffInWeeks = Math.floor(targetWeekSunday.diff(currentWeekSunday, "weeks").weeks);
					setWeeks(Math.max(0, diffInWeeks));
				}
			}

			const hasAutoPost = scheduleCycleWeeks != null && schedulePublishLeadWeeks != null;
			setAutoPublishEnabled(hasAutoPost);
			setCycleWeeks(scheduleCycleWeeks ?? 4);
			setLeadWeeks(schedulePublishLeadWeeks ?? 2);
		}
	}, [open, currentDate, scheduleCycleWeeks, schedulePublishLeadWeeks]);

	const handleWeeksChange = (value: number) => {
		setWeeks(Math.max(0, value));
	};

	const handlePublish = async () => {
		try {
			setIsLoading(true);
			const currentWeekSunday = DateTime.now().startOf("week");
			const targetWeekSunday = currentWeekSunday.plus({ weeks: weeks });
			const targetDate = targetWeekSunday.endOf("week");

			await axiosInstance.put(`/departments/${departmentId}/staff-schedule-display-until-date`, {
				date: targetDate.toFormat("yyyy-MM-dd"),
				notification_message: notificationMessage.trim() || undefined,
			});

			await updateUserInfo();
			await afterScheduleWrite({ allWeeks: true });
			showSnackbar("Staff schedule visibility updated successfully", "success");
			onClose();
		} catch (error) {
			showErrorSnackbar("Failed to update staff schedule visibility", {}, error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleSaveAutoPost = async () => {
		try {
			setIsSavingAutoPost(true);

			await axiosInstance.put(`/departments/${departmentId}/auto-post-schedule-config`, {
				schedule_cycle_weeks: autoPublishEnabled ? cycleWeeks : null,
				schedule_publish_lead_weeks: autoPublishEnabled ? leadWeeks : null,
			});

			await updateUserInfo();
			await afterScheduleWrite({ allWeeks: true });
			showSnackbar(autoPublishEnabled ? "Automatic publishing enabled" : "Automatic publishing disabled", "success");
		} catch (error) {
			showErrorSnackbar("Failed to update automatic publishing settings", {}, error);
		} finally {
			setIsSavingAutoPost(false);
		}
	};

	const currentWeekSunday = DateTime.now().startOf("week");
	const targetWeekSunday = currentWeekSunday.plus({ weeks: weeks });
	const targetDate = targetWeekSunday.endOf("week");

	const currentDisplayDate = currentDate ? (typeof currentDate === "string" ? DateTime.fromISO(currentDate) : currentDate) : null;

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ color: INK.SECONDARY, fontWeight: 600, fontSize: "24px" }}>Staff Schedule Visibility</DialogTitle>
			<DialogContent>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
					{currentDisplayDate?.isValid && (
						<Box
							sx={{
								backgroundColor: NEUTRAL[100],
								padding: 2,
								borderRadius: 1,
								borderLeft: `4px solid ${INK.SECONDARY}`,
							}}
						>
							<Typography sx={{ color: INK.MUTED, fontSize: "12px", fontWeight: 500, mb: 0.5 }}>Current Visibility:</Typography>
							<Typography sx={{ color: INK.SECONDARY, fontSize: "16px", fontWeight: 600 }}>
								Staff can currently see schedule until <strong>{currentDisplayDate.toFormat("MMMM dd, yyyy")}</strong>
							</Typography>
						</Box>
					)}
					<Typography sx={{ color: INK.SECONDARY, fontWeight: 500, fontSize: "14px" }}>
						Select how many weeks in the future staff users can see the schedule:
					</Typography>
					<TextField
						type="number"
						label="Weeks"
						value={weeks}
						onChange={(e) => handleWeeksChange(parseInt(e.target.value) || 0)}
						fullWidth
						slotProps={{
							htmlInput: { min: 0 },
						}}
					/>
					<Typography sx={{ color: INK.SECONDARY, fontSize: "14px" }}>
						Staff will see schedule until: <strong>{targetDate.toFormat("MMMM dd, yyyy")}</strong>
					</Typography>
					<Typography sx={{ color: INK.SECONDARY, fontWeight: 500, fontSize: "14px", mt: 2 }}>Notification Message (optional):</Typography>
					<TextField
						multiline
						rows={4}
						label="Message to send to staff in the schedule"
						value={notificationMessage}
						onChange={(e) => setNotificationMessage(e.target.value)}
						placeholder="Enter a message to notify staff members about the schedule publication..."
						fullWidth
					/>
					<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
						<SystemButton onClick={handlePublish} disabled={isLoading} loading={isLoading}>
							Publish
						</SystemButton>
					</Box>

					<Divider sx={{ my: 1 }} />

					<Typography sx={{ color: INK.SECONDARY, fontWeight: 600, fontSize: "18px" }}>Automatic Publishing</Typography>
					<Typography sx={{ color: INK.MUTED, fontSize: "13px" }}>When enabled, the schedule will be automatically published on a recurring cycle.</Typography>

					<FormControlLabel
						control={<Switch checked={autoPublishEnabled} onChange={(e) => setAutoPublishEnabled(e.target.checked)} />}
						label="Enable automatic publishing"
						sx={{ color: INK.SECONDARY }}
					/>

					{autoPublishEnabled && (
						<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
							<TextField
								type="number"
								label="Schedule cycle length (weeks)"
								value={cycleWeeks}
								onChange={(e) => setCycleWeeks(Math.max(1, parseInt(e.target.value, 10) || 1))}
								fullWidth
								helperText="How many weeks each published schedule covers"
								slotProps={{
									htmlInput: { min: 1 },
								}}
							/>
							<TextField
								type="number"
								label="Publish lead time (weeks)"
								value={leadWeeks}
								onChange={(e) => setLeadWeeks(Math.max(1, parseInt(e.target.value, 10) || 1))}
								fullWidth
								helperText="How many weeks before the current period ends to publish the next one"
								slotProps={{
									htmlInput: { min: 1 },
								}}
							/>
						</Box>
					)}

					<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
						<SystemButton onClick={handleSaveAutoPost} disabled={isSavingAutoPost} loading={isSavingAutoPost}>
							{autoPublishEnabled ? "Save Auto-Publish Settings" : "Disable Auto-Publish"}
						</SystemButton>
					</Box>
				</Box>
			</DialogContent>
		</Dialog>
	);
};
